// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL, AUTH_BASE_URL, CLIENT_HEADER } from "../config";
import { clearAppStorage } from "../services/storage";

const AuthContext = createContext();

// /Auth/validate errors the login page explains instead of silently offering sign-in again: the
// identity signed in but has no Axaris account (401), or its account is deactivated (403).
const ACCOUNT_ERROR_CODES = ["user_not_found", "user_inactive"];
const accountErrorFrom = (e) => {
  const data = e?.response?.data;
  const code = data?.error;
  if (!ACCOUNT_ERROR_CODES.includes(code)) return null;
  return { code, email: data?.email || null, message: data?.message || null };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [orgHierarchy, setOrgHierarchy] = useState([]);
  const [dealHierarchy, setDealHierarchy] = useState([]);
  const [businessUnitHierarchy, setBusinessUnitHierarchy] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSupportAdmin, setIsSupportAdmin] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [authVersion, setAuthVersion] = useState(0); // Increment on (re)auth to trigger re-fetches
  // { code, email, message } when the bootstrap /Auth/validate said user_not_found / user_inactive;
  // the user is still signed out. Cleared by the next successful validate and by logout.
  const [authError, setAuthError] = useState(null);

  // Apply an /Auth/validate response to context state + localStorage mirrors (read by some
  // components and useRBAC). The session credential itself is the HttpOnly cookie — never in JS.
  const applyValidate = useCallback((data) => {
    const roles = data?.userRoles || [];
    const hierarchy = data?.orgHierarchy || [];
    const dHierarchy = data?.dealHierarchy || [];
    const buHierarchy = data?.businessUnitHierarchy || [];
    const superAdmin = data?.isSuperAdmin || false;
    const supportAdmin = data?.isSupportAdmin || false;

    setAuthError(null);
    setUser(data);
    setUserRoles(roles);
    setOrgHierarchy(hierarchy);
    setDealHierarchy(dHierarchy);
    setBusinessUnitHierarchy(buHierarchy);
    setIsSuperAdmin(superAdmin);
    setIsSupportAdmin(supportAdmin);

    try {
      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("userRoles", JSON.stringify(roles));
      localStorage.setItem("orgHierarchy", JSON.stringify(hierarchy));
      localStorage.setItem("dealHierarchy", JSON.stringify(dHierarchy));
      localStorage.setItem("businessUnitHierarchy", JSON.stringify(buHierarchy));
      localStorage.setItem("isSuperAdmin", JSON.stringify(superAdmin));
      localStorage.setItem("isSupportAdmin", JSON.stringify(supportAdmin));
    } catch (e) {
      console.error("AuthContext: failed to persist auth state", e);
    }
  }, []);

  // Bootstrap: ask the backend who we are using the session cookie. 401 => not signed in.
  // Uses a plain axios call (not the `api` instance) so the global 401 interceptor does not
  // fire during initial load — PrivateRoute decides where to send an unauthenticated user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/Auth/validate`, {
          withCredentials: true,
          headers: { [CLIENT_HEADER]: "1" },
        });
        if (!cancelled && res.status === 200) {
          applyValidate(res.data);
          setAuthVersion(v => v + 1);
        }
      } catch (e) {
        // 401 (no session) or any error => treat as signed out. An account error is kept so the
        // login page can say why (the session exists, but no active Axaris user owns it).
        if (!cancelled) {
          setAuthError(accountErrorFrom(e));
          setUser(null);
          clearAppStorage();
        }
      } finally {
        if (!cancelled) setInitialized(true);
      }
    })();
    return () => { cancelled = true; };
  }, [applyValidate]);

  // For flows that already hold a fresh /Auth/validate payload (e.g. invite acceptance).
  const login = useCallback((userData) => {
    applyValidate(userData);
    setAuthVersion(v => v + 1);
  }, [applyValidate]);

  const refreshUserRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/Auth/validate`, {
        withCredentials: true,
        headers: { [CLIENT_HEADER]: "1" },
      });
      if (res.status === 200) {
        applyValidate(res.data);
        setAuthVersion(v => v + 1);
      }
    } catch (e) {
      console.error("refreshUserRoles: error", e);
    }
  }, [applyValidate]);

  const logout = useCallback(() => {
    setAuthError(null);
    setUser(null);
    setUserRoles([]);
    setOrgHierarchy([]);
    setDealHierarchy([]);
    setBusinessUnitHierarchy([]);
    setIsSuperAdmin(false);
    setIsSupportAdmin(false);
    clearAppStorage();
    // Clear the server session (+ Entra session) and return to the SPA login page.
    const returnUrl = `${window.location.origin}/login`;
    window.location.href = `${AUTH_BASE_URL}/auth/logout?returnUrl=${encodeURIComponent(returnUrl)}`;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      try { localStorage.setItem("user", JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  }, []);

  // Compatibility shim: existing code that checks `token` truthiness keeps working. The real
  // credential is the HttpOnly cookie, opaque to JavaScript.
  const token = user ? "cookie" : null;
  const isAuthenticated = !!user;

  const value = useMemo(() => ({
    user,
    token,
    userRoles,
    orgHierarchy,
    setOrgHierarchy,
    dealHierarchy,
    businessUnitHierarchy,
    isSuperAdmin,
    isSupportAdmin,
    login,
    logout,
    updateUser,
    refreshUserRoles,
    initialized,
    isAuthenticated,
    authVersion,
    authError
  }), [user, token, userRoles, orgHierarchy, dealHierarchy, businessUnitHierarchy, isSuperAdmin, isSupportAdmin, login, logout, updateUser, refreshUserRoles, initialized, isAuthenticated, authVersion, authError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
